<template>
    <div v-if="!hideGuide || child_subs.length" :style="{
        marginTop: sub_data_contents.margin_top ? sub_data_contents.margin_top + 'px' : '0px',
        marginBottom: sub_data_contents.margin_bottom ? sub_data_contents.margin_bottom + 'px' : '3rem'
    }">
        <div v-if="!hideGuide">
            <template v-if="sub_data_contents.is_toggle">
                <b-button

                    :id="`guide-text2-toggle-btn-${ sub_code }`"
                    class="px-2 py-1"
                    @click="toggleCollapse"
                    v-b-toggle="`guide-text2-toggle-${sub_code}`"
                >
                    <fa icon="question-circle"></fa>
                    {{ getLocaleToggleText }}
                </b-button>
                <!-- textbox 유형 Sub Component -->
                <b-collapse v-model="collapsed"
                            :id="`guide-text2-toggle-${sub_code}`"
                >
                    <div :class="sub_data_contents.bordered == true ? 'bordered-guide-text' : ''">
                        <div v-if="sub_data_contents.html == true">
                            <span v-html="getLocaleGuideText"></span>
                        </div>
                        <div v-else> {{ getLocaleGuideText }}</div>
                    </div>
                </b-collapse>
            </template>
            <template v-else>
                <div :class="sub_data_contents.bordered == true ? 'bordered-guide-text' : ''">
                    <div v-if="sub_data_contents.html == true">
                        <span v-html="getLocaleGuideText"></span>
                    </div>
                    <div v-else> {{ getLocaleGuideText }}</div>
                </div>
            </template>
        </div>
        <!-- Child Component -->
        <sub-component @update-parent-contents="handleUpdateParentContents"
                       :disabled="is_locked || is_disabled_by_condition || is_disabled_by_parent"
                       v-for="item in child_subs" :key="item.id" :sub_type="item.sub_type" :item="item"></sub-component>
    </div>
</template>

<script>
import {SubComponentMixin} from '@core_mixins/SubComponentMixin'

// 코드 가이드 배너(guide_text2)를 협력사 답변 화면에서 숨길 프로젝트 코드(대문자).
// 비어 있으면 어디서도 숨기지 않는다 — 기본값은 "원안 그대로 표시"다.
// 넥센타이어 서면평가 3종 + 자가진단(실운영 NEXTIER-SELF, 테스트 NEXTIER-SELF_TEST).
// 다른 프로젝트(철강협회 등)는 질문지를 공유해도 여기 없으면 그대로 보인다.
const HIDE_CODE_GUIDE_PROJECT_CODES = ['NEXTIER-S', 'NEXTIER-M', 'NEXTIER-L', 'NEXTIER-SELF', 'NEXTIER-SELF_TEST']

// 배너를 식별하는 본문 표식. 배너는 이 경로의 이미지 하나만 담고 있다.
const CODE_GUIDE_PATH = '/images/code_guide/'

export default {
    name: 'df-guide-text2',
    mixins: [SubComponentMixin],
    data() {
        return {
            collapsed: false,
        }
    },
    created() {
        if (this.sub_data_contents.toggled) {
            this.collapsed = true
        } else {
            this.collapsed = false
        }
    },
    computed: {
        /**
         * 이 안내문이 코드 가이드 배너인가 — 본문이 /images/code_guide/ 이미지를 담고 있는가.
         *
         * 이미지가 실제로 로드되는지는 보지 않는다. 브랜드가 단일 빌드·단일 버킷으로 합쳐진 뒤로
         * 이미지 유효 여부는 전 브랜드 공통 값이라 프로젝트를 가르는 정보를 담지 못한다.
         * 로케일 배열 전체를 보는 이유는 번역 칸이 비어 있거나 다를 수 있기 때문이다.
         */
        isCodeGuideBanner() {
            const _structure = this.sub_data_contents && this.sub_data_contents.structure
            return Array.isArray(_structure)
                && _structure.some(v => typeof v === 'string' && v.indexOf(CODE_GUIDE_PATH) > -1)
        },
        /**
         * 코드 가이드 배너를 숨긴다. 세 조건을 모두 만족할 때만.
         *
         * - 답변 화면과 평가/심사 화면(audit_mode) 모두에서 숨긴다. 평가화면도 프로젝트가 갈리므로
         *   (/answers/:id 진입 경로가 전부 pc=project_code 를 붙인다) 넥센만 숨고 나머지는 그대로다.
         * - 문항 편집(is_edit_page)은 **어떤 프로젝트든 원안 그대로** 보여준다. 편집자는 본문을 봐야 고친다.
         * - 배너가 아닌 안내문(텍스트 안내 등)은 건드리지 않는다.
         * - 질문지는 프로젝트끼리 공유되므로 문항이 아니라 **프로젝트 코드**가 분리 축이다.
         */
        hideGuide() {
            if (this.is_edit_page) return false
            if (!this.isCodeGuideBanner) return false
            const _code = this.$route?.query?.pc
                ?? this.$store?.state?.campaign_in_progress?.project_code
            // 프로젝트 코드에 앞뒤 공백이 섞여 들어오는 경우가 있어 trim 한다.
            return !!_code && HIDE_CODE_GUIDE_PROJECT_CODES.includes(String(_code).trim().toUpperCase())
        },
        textarea_rows: {
            get() {
                if (this.sub_data_header?.rows && this.sub_data_header.rows != 0) {
                    return this.sub_data_header.rows
                } else {
                    return 8
                }
            }
        },
        getLocaleGuideText() {
            if (this.$i18n.locale === 'ko') {
                return this.sub_data_contents.structure[0]
            } else if (this.$i18n.locale === 'en') {
                return this.sub_data_contents.structure[1]
            } else if (this.$i18n.locale === 'ca') {
                return this.sub_data_contents.structure[2]
            }
        },
        getLocaleToggleText() {
            if (this.$i18n.locale === 'ko') {
                return this.sub_data_contents.toggle_title[0]
            } else if (this.$i18n.locale === 'en') {
                return this.sub_data_contents.toggle_title[1]
            } else if (this.$i18n.locale === 'ca') {
                return this.sub_data_contents.toggle_title[2]
            }
        },
        toggleCollapse() {
            this.collapsed = !this.collapsed
        }
    }
}
</script>

<style>
.bordered-guide-text {
    padding: 1rem 1.75rem;
    background-color: var(--gray-200);
    border: 1px solid rgba(0, 0, 0, 0.125);
    border-radius: 0.25rem;
}
</style>
